/**
 * Keeps keyboard focus sensible when the bag re-renders (drawer and /cart), and
 * announces the change. Horizon morphs the section after every quantity change,
 * which leaves focus on <body>.
 *
 * - +/- or typing a quantity: focus stays on that line's control.
 * - Remove: focus moves to the next line's title link, else the previous line's,
 *   else the empty-state heading.
 * - After any change a polite live region says "Bag updated, N items".
 *
 * Listens for the click, waits for Horizon to finish (the `cart-items-disabled`
 * class comes off the cart-items-component), then restores focus. No Horizon
 * file is patched.
 */

const SETTLE_TIMEOUT = 6000;

/** @type {{ kind: 'qty' | 'remove', drawer: boolean, key: string, control: string, keys: string[], index: number, sawBusy: boolean } | null} */
let pending = null;
/** @type {MutationObserver | null} */
let observer = null;
/** @type {number | undefined} */
let timer;

/** @param {Element} el */
const rowOf = (el) => /** @type {HTMLElement | null} */ (el.closest('.cart-items__table-row'));

/** @param {Element} el */
const componentOf = (el) => /** @type {HTMLElement | null} */ (el.closest('cart-items-component'));

/** @param {HTMLElement} component */
const rowKeys = (component) =>
  Array.from(component.querySelectorAll('.cart-items__table-row[data-key]')).map((row) => /** @type {HTMLElement} */ (row).dataset.key ?? '');

function stop() {
  observer?.disconnect();
  observer = null;
  window.clearTimeout(timer);
  pending = null;
}

/** @param {boolean} drawer */
function currentComponent(drawer) {
  const all = Array.from(document.querySelectorAll('cart-items-component'));
  return /** @type {HTMLElement | undefined} */ (all.find((c) => (/** @type {HTMLElement} */ (c).dataset.drawer !== undefined) === drawer));
}

/** @param {HTMLElement | null | undefined} el */
function focusEl(el) {
  if (!el) return false;
  el.focus({ preventScroll: false });
  return document.activeElement === el;
}

function announce(component) {
  const region = document.querySelector('[data-saadhu-bag-status]');
  if (!(region instanceof HTMLElement)) return;
  const countEl = component?.querySelector('[ref="cartItemCount"]');
  const count = countEl ? parseInt(countEl.textContent ?? '0', 10) || 0 : 0;
  const text =
    count === 1 ? region.dataset.textOne : (region.dataset.text ?? '').replace('__count__', String(count));
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = text ?? '';
  }, 60);
}

function restore() {
  if (!pending) return;
  const intent = pending;
  const component = currentComponent(intent.drawer);
  if (!component) return stop();

  if (intent.kind === 'remove') {
    const rows = Array.from(component.querySelectorAll('.cart-items__table-row[data-key]'));
    const live = new Set(rows.map((row) => /** @type {HTMLElement} */ (row).dataset.key));
    // The removed row (and any nested lines) must be gone before we pick a target.
    if (live.has(intent.key) && !component.querySelector('[data-cart-drawer-empty]')) {
      // Still animating out; look again shortly (stop() cancels via the timeout).
      window.setTimeout(restore, 100);
      return;
    }

    const after = intent.keys.slice(intent.index + 1).find((k) => live.has(k));
    const before = intent.keys.slice(0, intent.index).reverse().find((k) => live.has(k));
    const targetKey = after ?? before;
    const row = rows.find((r) => /** @type {HTMLElement} */ (r).dataset.key === targetKey);
    if (row) {
      focusEl(row.querySelector('.cart-items__title'));
    } else {
      const heading = intent.drawer
        ? document.getElementById('cart-drawer-heading')
        : document.querySelector('.cart-page--empty h1, .cart-page h1');
      focusEl(/** @type {HTMLElement | null} */ (heading));
    }
  } else {
    const row = Array.from(component.querySelectorAll('.cart-items__table-row[data-key]')).find(
      (r) => /** @type {HTMLElement} */ (r).dataset.key === intent.key
    );
    const active = document.activeElement;
    const lost = !active || active === document.body || !component.contains(active);
    if (row && lost) {
      const preferred = row.querySelector(`[name="${intent.control}"]`);
      const input = row.querySelector('input[name="updates[]"]');
      // A minus button that just hit the minimum is disabled; fall back to the quantity field.
      if (!(preferred instanceof HTMLButtonElement && !preferred.disabled && focusEl(preferred))) {
        focusEl(/** @type {HTMLElement | null} */ (input));
      }
    }
  }

  announce(component);
  stop();
}

/** @param {typeof pending} intent */
function watch(intent) {
  stop();
  pending = intent;
  observer = new MutationObserver(() => {
    if (!pending) return;
    const component = currentComponent(pending.drawer);
    if (!component) return;
    if (component.classList.contains('cart-items-disabled')) {
      pending.sawBusy = true;
    } else if (pending.sawBusy) {
      // Let the morph and the removal animation finish before moving focus.
      window.setTimeout(restore, 120);
      observer?.disconnect();
      observer = null;
    }
  });
  observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
  timer = window.setTimeout(stop, SETTLE_TIMEOUT);
}

/** @param {Element} control the button or input the shopper used */
function begin(control, kind) {
  const row = rowOf(control);
  const component = componentOf(control);
  if (!row || !component) return;
  const keys = rowKeys(component);
  const key = row.dataset.key ?? '';
  watch({
    kind,
    drawer: component.dataset.drawer !== undefined,
    key,
    control: /** @type {HTMLElement} */ (control).getAttribute('name') ?? '',
    keys,
    index: keys.indexOf(key),
    sawBusy: false,
  });
}

document.addEventListener(
  'click',
  (event) => {
    if (!(event.target instanceof Element)) return;
    const remove = event.target.closest('.cart-items__remove');
    if (remove) return begin(remove, 'remove');
    const step = event.target.closest('.quantity-minus, .quantity-plus');
    if (step && step.closest('cart-items-component')) begin(step, 'qty');
  },
  true
);

document.addEventListener(
  'change',
  (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.name === 'updates[]' && input.closest('cart-items-component')) {
      begin(input, 'qty');
    }
  },
  true
);
